import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, Megaphone, AlertTriangle, Flame } from "lucide-react";

const PRIO_ICON = { normal: Megaphone, important: AlertTriangle, urgent: Flame };
const POSITIONS = ["Apotheker/in", "PTA", "PKA", "Botenfahrer", "Reinigungskraft", "Student", "Praktikant", "Filialleiter"];

export default function AnnouncementsPage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", priority: "normal", location_ids: [], target_positions: [], target_roles: [] });

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/comms/announcements"), api.get("/locations")]);
    setItems(r1.data);
    setLocations(r2.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/comms/announcements", form); toast.success("Veröffentlicht"); setDialog(false); setForm({ title: "", content: "", priority: "normal", location_ids: [], target_positions: [], target_roles: [] }); await load(); }
    catch (err) { toast.error("Fehler"); }
  };
  const markRead = async (id) => { await api.post(`/comms/announcements/${id}/read`); await load(); };
  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/comms/announcements/${id}`); await load(); };

  const togglePos = (p) => {
    const set = new Set(form.target_positions);
    set.has(p) ? set.delete(p) : set.add(p);
    setForm({ ...form, target_positions: Array.from(set) });
  };
  const toggleLoc = (id) => {
    const set = new Set(form.location_ids);
    set.has(id) ? set.delete(id) : set.add(id);
    setForm({ ...form, location_ids: Array.from(set) });
  };

  return (
    <div className="space-y-6" data-testid="ann-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Team</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Schwarzes Brett</h2>
        </div>
        {isManager ? (
          <Button data-testid="ann-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Ankündigung</Button>
        ) : (
          <Button data-testid="ann-add-btn" variant="outline" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Beitrag</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((a) => {
          const Icon = PRIO_ICON[a.priority] || Megaphone;
          const readByMe = a.read_by?.includes(user.id);
          return (
            <Card key={a.id} data-testid={`ann-${a.id}`} className={`card-hover border-border/60 ${a.priority !== "normal" ? "border-l-4" : ""}`} style={{ borderLeftColor: a.priority === "urgent" ? "#DC2626" : a.priority === "important" ? "#D97706" : undefined }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${a.priority === "urgent" ? "text-rose-600" : a.priority === "important" ? "text-amber-600" : "text-blue-600"}`} />
                    <h3 className="font-heading font-bold text-lg">{a.title}</h3>
                  </div>
                  {a.priority !== "normal" && <Badge variant="outline" className="uppercase text-[10px]">{a.priority}</Badge>}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap mb-3">{a.content}</p>
                {(a.target_positions?.length > 0 || a.location_ids?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {a.target_positions?.map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                    {a.location_ids?.map(lid => {
                      return <Badge key={lid} variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/20">Standort</Badge>;
                    })}
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-border/60">
                  <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString("de-DE")} · {a.read_by?.length || 0} gelesen</div>
                  <div className="flex gap-2">
                    {!readByMe && <Button data-testid={`ann-read-${a.id}`} size="sm" variant="outline" onClick={() => markRead(a.id)}><CheckCircle2 className="w-3 h-3 mr-1" /> Gelesen</Button>}
                    {readByMe && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">✓ Gelesen</Badge>}
                    {(isManager || a.user_id === user.id) && <Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Keine Ankündigungen</div>}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Neue Ankündigung</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Titel" data-testid="ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea placeholder="Inhalt" data-testid="ann-content" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <div>
              <label className="text-sm font-medium mb-1 block">Priorität</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="important">Wichtig</SelectItem>
                  <SelectItem value="urgent">Dringend (Rote-Hand-Brief)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Zielgruppe – Positionen (leer = alle)</label>
              <div className="flex flex-wrap gap-1.5">
                {POSITIONS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`ann-pos-${p}`}
                    onClick={() => togglePos(p)}
                    className={`text-xs rounded-full px-3 py-1 border ${form.target_positions.includes(p) ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-800 border-border/60 hover:border-blue-400"}`}
                  >{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Standorte (leer = alle)</label>
              <div className="flex flex-wrap gap-1.5">
                {locations.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    data-testid={`ann-loc-${l.id}`}
                    onClick={() => toggleLoc(l.id)}
                    className={`text-xs rounded-full px-3 py-1 border ${form.location_ids.includes(l.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-800 border-border/60 hover:border-blue-400"}`}
                  >{l.name}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="ann-save" onClick={create}>Veröffentlichen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
